import { PageBuilderTextFieldPropertiesModel, PageBuilderTextPropertiesModel } from "@de-trustleap/shared-models";
import { BaseWidget } from "./baseWidget";

export class TextAreaWidget extends BaseWidget<PageBuilderTextFieldPropertiesModel> {
    private disableInlineStyles: boolean;

    constructor(properties: PageBuilderTextFieldPropertiesModel = new PageBuilderTextFieldPropertiesModel({}), disableInlineStyles = false) {
        super(properties);
        this.disableInlineStyles = disableInlineStyles;
    }

    async render(): Promise<HTMLElement> {
        const textarea = document.createElement('textarea');
        textarea.id = this.generateId();

        this.configureRows(textarea);
        if (!this.disableInlineStyles) {
            this.applyBaseStyles(textarea);
            this.applyTextStyles(textarea);
            this.applyPlaceholderStyles(textarea);
        } else {
            textarea.placeholder = this.properties.placeHolderTextProperties?.text ?? '';
        }

        if (this.properties.isRequired) {
            textarea.required = true;
        }

        this.attachDebounce(textarea);
        return textarea;
    }

    private generateId(): string {
        return `textarea-${crypto.randomUUID().slice(0, 8)}`;
    }

    private configureRows(textarea: HTMLTextAreaElement): void {
        const minLines = this.properties.minLines ?? 3;
        textarea.rows = minLines;
        if (!this.disableInlineStyles) {
            const maxLines = this.properties.maxLines ?? 10;
            const lineHeight = 1.5;
            textarea.style.maxHeight = `${maxLines * lineHeight}em`;
            textarea.style.overflowY = maxLines > minLines ? 'auto' : 'hidden';
        }
    }

    private applyBaseStyles(textarea: HTMLTextAreaElement): void {
        this.applyStyles(textarea, {
            width: this.properties.width ? `${this.properties.width}px` : 'auto',
            borderRadius: '5px',
            padding: '10px',
            boxSizing: 'border-box',
            resize: 'none',
            border: `1px solid ${this.formatColor(this.properties.borderColor, '#999999')}`,
            backgroundColor: this.formatColor(this.properties.backgroundColor, '#FFFFFF')
        });
    }

    private applyTextStyles(textarea: HTMLTextAreaElement): void {
        const textProps: PageBuilderTextPropertiesModel = this.properties.textProperties ?? new PageBuilderTextPropertiesModel({});
        const defaultLineHeight = 1.5;

        this.applyStyles(textarea, {
            fontSize: `${textProps.fontSize ?? 16}px`,
            color: this.formatColor(textProps.color, '#000000'),
            fontFamily: textProps.fontFamily ?? 'Arial, sans-serif',
            textAlign: textProps.alignment ?? 'left',
            lineHeight: `${textProps.lineHeight ?? defaultLineHeight}`
        });
    }

    private applyPlaceholderStyles(textarea: HTMLTextAreaElement): void {
        const placeholderProps = this.properties.placeHolderTextProperties;
        if (!placeholderProps) return;

        textarea.placeholder = placeholderProps.text ?? '';
        textarea.style.setProperty('--placeholder-color', this.formatColor(placeholderProps.color, '#999999'));
        textarea.style.setProperty('--placeholder-font-size', `${placeholderProps.fontSize ?? 16}px`);
        textarea.style.setProperty('--placeholder-font-family', placeholderProps.fontFamily ?? 'Arial, sans-serif');
        textarea.style.setProperty('--placeholder-alignment', placeholderProps.alignment ?? 'left');

        if (!document.getElementById('textarea-placeholder-style')) {
            const style = document.createElement('style');
            style.id = 'textarea-placeholder-style';
            style.textContent = `
                textarea::placeholder {
                    color: var(--placeholder-color);
                    font-size: var(--placeholder-font-size);
                    font-family: var(--placeholder-font-family);
                    text-align: var(--placeholder-alignment);
                }
            `;
            document.head.appendChild(style);
        }
    }

    private attachDebounce(textarea: HTMLTextAreaElement): void {
        let debounceTimeout: number;
        textarea.addEventListener("input", () => {
            clearTimeout(debounceTimeout);
            debounceTimeout = window.setTimeout(() => {
                // Optional: künftige Input-Handling-Logik
            }, 300);
        });
    }
}
